/** @jsxImportSource @emotion/react */
import { StatisticsDateRangePicker } from '../../components/StatisticsDateRangePicker';
import { useStatisticsDateRange } from '../../hooks/useStatisticsDateRange';
import { css } from '@emotion/react';
import React, { useState, useEffect, useCallback, useRef, useMemo, useImperativeHandle, forwardRef } from 'react';
import {
  Select, Card, DatePicker, Input, message, Spin, Button, Typography, Row, Col, Space, Form,
} from 'antd';
import { DownOutlined, SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import ReactECharts from 'echarts-for-react';
import { AccessSessionTable } from '../components/AccessSessionTable';
import {
  getAccessUserChartData, getAccessUserListData, getAccessUserCustomListData, exportAccessUserExcel,
  AccessListResponse, AccessUserChartParams, AccessExportMeta, AccessExportFilterItem,
} from '../../services/statistics.service';
import {
  AGE_GROUP_OPTIONS, FEATURE_OPTIONS, GENDER_OPTIONS, DEVICE_OPTIONS, PROVINCE_OPTIONS,
} from './filterOptions';
import {
  buildAccessUserChartOption, getAccessUserChartTitle, PRIMARY_BAR_COLOR,
} from '../components/accessChartOptions';

const { Title, Text } = Typography;

const VIEW_BY_OPTIONS = [
  { value: 'age', label: 'Độ tuổi' },
  { value: 'gender', label: 'Giới tính' },
  { value: 'province', label: 'Tỉnh thành' },
  { value: 'feature', label: 'Chức năng hệ thống' },
  { value: 'age_gender', label: 'Độ tuổi và giới tính' },
  { value: 'age_province', label: 'Độ tuổi và tỉnh thành' },
  { value: 'feature_gender', label: 'Chức năng hệ thống và giới tính' },
  { value: 'feature_age', label: 'Chức năng hệ thống và độ tuổi' },
  { value: 'feature_province', label: 'Chức năng hệ thống và tỉnh thành' },
  { value: 'custom', label: 'Danh sách tùy chỉnh' },
];

export interface StatisticsAccessUserPageHandle {
  handleExport: () => Promise<void>;
}

interface Props {
  initialPeriod?: string;
  initialDateRange?: [string | null, string | null];
  subTabs?: React.ReactNode;
  onExportAvailabilityChange?: (ready: boolean) => void;
}

export const StatisticsAccessUserPage = forwardRef<StatisticsAccessUserPageHandle, Props>(
  ({ initialPeriod = 'all', initialDateRange = [null, null], subTabs, onExportAvailabilityChange }, ref) => {
    const [viewBy, setViewBy] = useState('age');
    const { period, dateRange, changePeriod: handlePeriodChange,
      applyDateRange } = useStatisticsDateRange(initialPeriod, initialDateRange);
    const chartVersion = useRef(0);
    const listVersion = useRef(0);
    const [chartData, setChartData] = useState<any>(null);
    const [listData, setListData] = useState<AccessListResponse | undefined>();
    const [chartLoading, setChartLoading] = useState(false);
    const [listLoading, setListLoading] = useState(false);
    const [chartFailed, setChartFailed] = useState(false);
    const [listFailed, setListFailed] = useState(false);
    const [, setExportLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const chartRef = useRef<any>(null);

    // Chart filters
    const [filterAgeGroups, setFilterAgeGroups] = useState<string[]>([]);
    const [filterGenders, setFilterGenders] = useState<string[]>([]);
    const [filterProvinces, setFilterProvinces] = useState<string[]>([]);
    const [filterFeatures, setFilterFeatures] = useState<string[]>([]);

    // Custom list filters
    const [customName, setCustomName] = useState('');
    const [customPhone, setCustomPhone] = useState('');
    const [customEmail, setCustomEmail] = useState('');
    const [customUserIds, setCustomUserIds] = useState('');
    const [customAccessRange, setCustomAccessRange] = useState<[string | null, string | null]>([null, null]);
    const [customGenders, setCustomGenders] = useState<string[]>([]);
    const [customAgeGroups, setCustomAgeGroups] = useState<string[]>([]);
    const [customProvinces, setCustomProvinces] = useState<string[]>([]);
    const [customFeatures, setCustomFeatures] = useState<string[]>([]);
    const [customDevices, setCustomDevices] = useState<string[]>([]);
    const [appliedCustomFilters, setAppliedCustomFilters] = useState<any>({});

    const isCustom = viewBy === 'custom';

    const handleCustomSearch = () => {
      const idList = customUserIds.split(/[,;]/).map(s => s.trim()).filter(Boolean);
      if (idList.length > 5) {
        message.warning('Chỉ được tìm kiếm tối đa 5 User ID cùng lúc');
        return;
      }
      setPage(1);
      setAppliedCustomFilters({
        name: customName || undefined,
        phone: customPhone || undefined,
        email: customEmail || undefined,
        genders: customGenders.length ? customGenders : undefined,
        age_groups: customAgeGroups.length ? customAgeGroups : undefined,
        provinces: customProvinces.length ? customProvinces : undefined,
        features: customFeatures.length ? customFeatures : undefined,
        devices: customDevices.length ? customDevices : undefined,
        user_ids: customUserIds || undefined,
        access_from: customAccessRange[0] || undefined,
        access_to: customAccessRange[1] || undefined,
      });
    };

    const handleCustomReset = () => {
      setCustomName('');
      setCustomPhone('');
      setCustomEmail('');
      setCustomUserIds('');
      setCustomAccessRange([null, null]);
      setCustomGenders([]);
      setCustomAgeGroups([]);
      setCustomProvinces([]);
      setCustomFeatures([]);
      setCustomDevices([]);
      setPage(1);
      setAppliedCustomFilters({});
    };

    const fetchChart = useCallback(async () => {
      const _isCustom = viewBy === 'custom';
      if (_isCustom) return;
      const version = ++chartVersion.current;
      setChartFailed(false);
      setChartLoading(true);
      const params: AccessUserChartParams = {
        period: period !== 'custom' ? period : undefined,
        from_date: period === 'custom' ? (dateRange[0] || undefined) : undefined,
        to_date: period === 'custom' ? (dateRange[1] || undefined) : undefined,
        view_by: viewBy,
        age_groups: filterAgeGroups,
        genders: filterGenders,
        provinces: filterProvinces,
        features: filterFeatures,
      };
      try {
        const data = await getAccessUserChartData(params);
        if (version !== chartVersion.current) return;
        setChartData(data);
      } catch {
        if (version !== chartVersion.current) return;
        setChartFailed(true);
        message.error('Không thể tải dữ liệu biểu đồ');
      } finally {
        if (version === chartVersion.current) setChartLoading(false);
      }
    }, [viewBy, period, dateRange, filterAgeGroups, filterGenders, filterProvinces, filterFeatures]);

    const fetchList = useCallback(async () => {
      const _isCustom = viewBy === 'custom';
      const version = ++listVersion.current;
      setListFailed(false);
      setListLoading(true);
      try {
        let res: AccessListResponse;
        if (_isCustom) {
          res = await getAccessUserCustomListData({
            period: period !== 'custom' ? period : undefined,
            from_date: period === 'custom' ? (dateRange[0] || undefined) : undefined,
            to_date: period === 'custom' ? (dateRange[1] || undefined) : undefined,
            ...appliedCustomFilters,
            page,
            limit: pageSize,
          });
        } else {
          res = await getAccessUserListData({
            period: period !== 'custom' ? period : undefined,
            from_date: period === 'custom' ? (dateRange[0] || undefined) : undefined,
            to_date: period === 'custom' ? (dateRange[1] || undefined) : undefined,
            view_by: viewBy,
            age_groups: filterAgeGroups,
            genders: filterGenders,
            provinces: filterProvinces,
            features: filterFeatures,
            page,
            limit: pageSize,
          });
        }
        if (version !== listVersion.current) return;
        setListData(res);
      } catch {
        if (version !== listVersion.current) return;
        setListFailed(true);

        message.error('Không thể tải danh sách');
      } finally {
        if (version === listVersion.current) setListLoading(false);
      }
    }, [viewBy, period, dateRange, filterAgeGroups, filterGenders, filterProvinces, filterFeatures, page, pageSize, appliedCustomFilters]);

    useEffect(() => {
      onExportAvailabilityChange?.(!chartLoading && !listLoading && !chartFailed && !listFailed);
    }, [chartLoading, listLoading, chartFailed, listFailed, onExportAvailabilityChange]);

    useEffect(() => { setPage(1); }, [viewBy, period, dateRange, filterAgeGroups, filterGenders, filterProvinces, filterFeatures]);
    useEffect(() => { fetchChart(); return () => { chartVersion.current += 1; }; }, [fetchChart]);
    useEffect(() => { fetchList(); return () => { listVersion.current += 1; }; }, [fetchList]);

    type SubFilterType = 'feature' | 'age' | 'gender' | 'province';
    let subFilter1: SubFilterType | null = null;
    let subFilter2: SubFilterType | null = null;

    if (viewBy === 'feature_gender') {
      subFilter1 = 'feature';
      subFilter2 = 'gender';
    } else if (viewBy === 'feature_age') {
      subFilter1 = 'feature';
      subFilter2 = 'age';
    } else if (viewBy === 'feature_province') {
      subFilter1 = 'feature';
      subFilter2 = 'province';
    } else if (viewBy === 'age_gender') {
      subFilter1 = 'age';
      subFilter2 = 'gender';
    } else if (viewBy === 'age_province') {
      subFilter1 = 'age';
      subFilter2 = 'province';
    } else if (viewBy === 'feature') {
      subFilter1 = 'feature';
    } else if (viewBy === 'age') {
      subFilter1 = 'age';
    } else if (viewBy === 'gender') {
      subFilter1 = 'gender';
    } else if (viewBy === 'province') {
      subFilter1 = 'province';
    }

    const handleExport = async () => {
      if (chartLoading || listLoading || chartFailed || listFailed) return;
      setExportLoading(true);
      const _isCustom = viewBy === 'custom';
      const exportParams: AccessUserChartParams = {
        period: period !== 'custom' ? period : undefined,
        from_date: period === 'custom' ? (dateRange[0] || undefined) : undefined,
        to_date: period === 'custom' ? (dateRange[1] || undefined) : undefined,
        view_by: viewBy,
        age_groups: filterAgeGroups,
        genders: filterGenders,
        provinces: filterProvinces,
        features: filterFeatures,
      };
      try {
        let chartImg: string | undefined;
        if (!_isCustom && chartRef.current) {
          try {
            chartImg = chartRef.current.getEchartsInstance().getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#ffffff' });
          } catch {
            // ignore
          }
        }

        const viewByLabel = VIEW_BY_OPTIONS.find((o) => o.value === viewBy)?.label || '';
        let chartTitle = getAccessUserChartTitle(viewBy);
        const filters: AccessExportFilterItem[] = [];

        if (_isCustom) {
          chartTitle = 'Thông tin bộ lọc tìm kiếm tuỳ chỉnh';
          if (appliedCustomFilters.name) {
            filters.push({ label: 'Họ và tên:', value: appliedCustomFilters.name });
          }
          if (appliedCustomFilters.phone) {
            filters.push({ label: 'Số điện thoại:', value: appliedCustomFilters.phone });
          }
          if (appliedCustomFilters.email) {
            filters.push({ label: 'Email:', value: appliedCustomFilters.email });
          }
          if (appliedCustomFilters.user_ids) {
            filters.push({ label: 'User ID:', value: appliedCustomFilters.user_ids });
          }
          if (appliedCustomFilters.access_from || appliedCustomFilters.access_to) {
            const from = appliedCustomFilters.access_from ? dayjs(appliedCustomFilters.access_from).format('DD/MM/YYYY') : '...';
            const to = appliedCustomFilters.access_to ? dayjs(appliedCustomFilters.access_to).format('DD/MM/YYYY') : '...';
            filters.push({ label: 'Thời điểm truy cập:', value: `${from} - ${to}` });
          }
          if (appliedCustomFilters.genders && appliedCustomFilters.genders.length > 0) {
            const genderStr = appliedCustomFilters.genders
              .map((g: string) => GENDER_OPTIONS.find((o: any) => o.value === g)?.label || g)
              .join(', ');
            filters.push({ label: 'Giới tính:', value: genderStr });
          }
          if (appliedCustomFilters.age_groups && appliedCustomFilters.age_groups.length > 0) {
            const ageStr = appliedCustomFilters.age_groups
              .map((a: string) => AGE_GROUP_OPTIONS.find((o: any) => o.value === a)?.label || a)
              .join(', ');
            filters.push({ label: 'Độ tuổi:', value: ageStr });
          }
          if (appliedCustomFilters.provinces && appliedCustomFilters.provinces.length > 0) {
            filters.push({ label: 'Tỉnh thành:', value: appliedCustomFilters.provinces.join(', ') });
          }
          if (appliedCustomFilters.features && appliedCustomFilters.features.length > 0) {
            const featStr = appliedCustomFilters.features
              .map((f: string) => FEATURE_OPTIONS.find((o: any) => o.value === f)?.label || f)
              .join(', ');
            filters.push({ label: 'Chức năng truy cập:', value: featStr });
          }
          if (appliedCustomFilters.devices && appliedCustomFilters.devices.length > 0) {
            const devStr = appliedCustomFilters.devices
              .map((d: string) => DEVICE_OPTIONS.find((o: any) => o.value === d)?.label || d)
              .join(', ');
            filters.push({ label: 'Thiết bị truy cập:', value: devStr });
          }
        } else {
          const getFilterItem = (type: string): AccessExportFilterItem | null => {
            if (type === 'feature') {
              const val = filterFeatures.length
                ? filterFeatures.map((id) => FEATURE_OPTIONS.find((o) => o.value === id)?.label || id).join(', ')
                : '-';
              return { label: 'Chức năng hệ thống:', value: val };
            }
            if (type === 'age') {
              const val = filterAgeGroups.length
                ? filterAgeGroups.map((id) => AGE_GROUP_OPTIONS.find((o) => o.value === id)?.label || id).join(', ')
                : '-';
              return { label: 'Độ tuổi:', value: val };
            }
            if (type === 'gender') {
              const val = filterGenders.length
                ? filterGenders.map((id) => GENDER_OPTIONS.find((o) => o.value === id)?.label || id).join(', ')
                : '-';
              return { label: 'Giới tính:', value: val };
            }
            if (type === 'province') {
              const val = filterProvinces.length
                ? filterProvinces.map((id) => PROVINCE_OPTIONS.find((o) => o.value === id)?.label || id).join(', ')
                : '-';
              return { label: 'Tỉnh thành:', value: val };
            }
            return null;
          };

          if (subFilter1) {
            const f1 = getFilterItem(subFilter1);
            if (f1) filters.push(f1);
          }
          if (subFilter2) {
            const f2 = getFilterItem(subFilter2);
            if (f2) filters.push(f2);
          }
        }

        let timeRangeLabel = 'Tất cả';
        if (period === 'today') {
          timeRangeLabel = `${dayjs().format('DD/MM/YYYY')} - ${dayjs().format('DD/MM/YYYY')}`;
        } else if (period === 'this_month') {
          timeRangeLabel = `${dayjs().startOf('month').format('DD/MM/YYYY')} - ${dayjs().endOf('month').format('DD/MM/YYYY')}`;
        } else if (period === 'this_year') {
          timeRangeLabel = `${dayjs().startOf('year').format('DD/MM/YYYY')} - ${dayjs().endOf('year').format('DD/MM/YYYY')}`;
        } else if (period === 'custom' && (dateRange[0] || dateRange[1])) {
          timeRangeLabel = `${dateRange[0] ? dayjs(dateRange[0]).format('DD/MM/YYYY') : '...'} - ${dateRange[1] ? dayjs(dateRange[1]).format('DD/MM/YYYY') : '...'}`;
        }

        const meta: AccessExportMeta = {
          tab_title: 'Lượt truy cập',
          view_by_label: viewByLabel,
          chart_title: chartTitle,
          filters,
          time_range_label: timeRangeLabel,
        };

        const blob = await exportAccessUserExcel(
          {
            ...exportParams,
            ...(_isCustom ? appliedCustomFilters : {}),
            page: 1,
            limit: 999999,
          },
          chartImg,
          _isCustom,
          meta
        );
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `thong_ke_nguoi_dung_${Date.now()}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
      } catch {
        message.error('Xuất Excel thất bại');
      } finally {
        setExportLoading(false);
      }
    };

    useImperativeHandle(ref, () => ({
      handleExport,
    }));

    const chartOption = useMemo(() => {
      return buildAccessUserChartOption(chartData);
    }, [chartData]);

    const isCombinedView = ['age_gender', 'age_province', 'feature_gender', 'feature_age', 'feature_province'].includes(viewBy);


    const renderFilterItem = (type: SubFilterType, isFlex1: boolean) => {
      let label = '';
      let placeholder = '';
      let value: string[] = [];
      let onChange: (val: string[]) => void = () => {};
      let options: any[] = [];
      let showSearch = false;

      switch (type) {
        case 'feature':
          label = 'Chức năng hệ thống';
          placeholder = 'Tất cả chức năng';
          value = filterFeatures;
          onChange = setFilterFeatures;
          options = FEATURE_OPTIONS;
          break;
        case 'age':
          label = 'Độ tuổi';
          placeholder = 'Tất cả độ tuổi';
          value = filterAgeGroups;
          onChange = setFilterAgeGroups;
          options = AGE_GROUP_OPTIONS;
          break;
        case 'gender':
          label = 'Giới tính';
          placeholder = 'Tất cả giới tính';
          value = filterGenders;
          onChange = setFilterGenders;
          options = GENDER_OPTIONS;
          break;
        case 'province':
          label = 'Tỉnh thành';
          placeholder = 'Tất cả tỉnh thành';
          value = filterProvinces;
          onChange = setFilterProvinces;
          options = PROVINCE_OPTIONS;
          showSearch = true;
          break;
      }

      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: isFlex1 ? 1 : '0 0 50%' }}>
          <span style={{ fontSize: 14, color: '#000000', whiteSpace: 'nowrap' }}>
            {label}
          </span>
          <Select
            mode="multiple"
            maxTagCount="responsive"
            allowClear
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            options={options}
            showSearch={showSearch}
            filterOption={
              showSearch
                ? (input, opt) =>
                    (opt?.label as string)?.toLowerCase().includes(input.toLowerCase())
                : undefined
            }
            style={{ width: '100%' }}
            dropdownMatchSelectWidth={false}
          />
        </div>
      );
    };

    return (
      <div>
        <Card css={mainCardStyle}>
          {/* Sub-tabs (Tổng quan / Người dùng / Tần suất / Thời gian sử dụng) */}
          {subTabs}

          {/* 1. "Xem theo ▾" Select */}
          <div css={viewByRowStyle}>
            <Select
              value={viewBy}
              onChange={(val) => {
                setViewBy(val);
                handlePeriodChange('all');
                setPage(1);
              }}
              labelRender={() => 'Xem theo'}
              style={{ minWidth: 150, maxWidth: '100%' }}
              className="xem-theo-select"
              suffixIcon={<DownOutlined style={{ color: '#fff' }} />}
              options={VIEW_BY_OPTIONS}
              dropdownMatchSelectWidth={false}
              placeholder="Xem theo"
            />
          </div>

          {/* 2. Chart Section (when viewBy is not custom) */}
          {!isCustom && (
            <div>
            <div css={headerStyle}>
              {/* Title & Sub-Filter Select(s) */}
              <div css={titleSectionStyle}>
                <Title level={4} style={{ margin: 0, marginBottom: 8, fontSize: 16 }}>
                  {getAccessUserChartTitle(viewBy)}
                </Title>
                {subFilter1 && (
                  <div style={{ display: 'flex', gap: '16px', padding: '0 12px' }}>
                    {renderFilterItem(subFilter1, isCombinedView && !!subFilter2)}
                    {subFilter2 && renderFilterItem(subFilter2, true)}
                  </div>
                )}
              </div>

              {/* Time Filters & RangePicker */}
              <div css={filterSectionStyle}>
                <Space size="small" wrap>
                  <Button
                    type={period === 'today' ? 'primary' : 'text'}
                    onClick={() => handlePeriodChange('today')}
                  >
                    Hôm nay
                  </Button>
                  <Button
                    type={period === 'this_month' ? 'primary' : 'text'}
                    onClick={() => handlePeriodChange('this_month')}
                  >
                    Tháng này
                  </Button>
                  <Button
                    type={period === 'this_year' ? 'primary' : 'text'}
                    onClick={() => handlePeriodChange('this_year')}
                  >
                    Năm này
                  </Button>
                  <Button
                    type={period === 'all' ? 'primary' : 'text'}
                    onClick={() => handlePeriodChange('all')}
                  >
                    Tất cả
                  </Button>
                  <StatisticsDateRangePicker value={dateRange} onChange={applyDateRange}
                    showApply />
                </Space>
              </div>
            </div>

            {/* Legend Indicator for Single Bar Views per Figma */}
            {['age', 'province', 'feature'].includes(viewBy) && chartData && (
              <div css={legendStyle}>
                <span css={legendBoxStyle} />
                <Text type="secondary" style={{ fontSize: 13 }}>
                  Số người dùng truy cập
                </Text>
              </div>
            )}

            <Spin spinning={chartLoading}>
              {chartData ? (
                <ReactECharts
                  ref={chartRef}
                  option={chartOption}
                  style={{ height: '500px', width: '100%' }}
                  notMerge={true}
                  lazyUpdate={false}
                />
              ) : (
                <div css={emptyStyle}>Không có dữ liệu</div>
              )}
            </Spin>
          </div>
        )}

        {/* 3. Custom List Filters per Figma */}
        {isCustom && (
          <div style={{ marginTop: 16 }}>
            <Title level={4} style={{ margin: 0, marginBottom: 20, fontSize: 16, fontWeight: 700 }}>
              Danh sách tuỳ chỉnh
            </Title>
            <Form layout="horizontal" labelAlign="left" labelCol={{ flex: '140px' }} wrapperCol={{ flex: 'auto' }} style={{ padding: '0 8px' }}>
              <Row gutter={40}>
                {/* Left Column per Figma */}
                <Col xs={24} lg={12}>
                  <Form.Item label="Họ và tên" labelAlign="left">
                    <Input placeholder="Nhập họ và tên" value={customName} onChange={e => setCustomName(e.target.value)} allowClear />
                  </Form.Item>
                  <Form.Item label="Email" labelAlign="left">
                    <Input placeholder="Nhập email" value={customEmail} onChange={e => setCustomEmail(e.target.value)} allowClear />
                  </Form.Item>
                  <Form.Item label="Độ tuổi" labelAlign="left">
                    <Select mode="multiple" placeholder="Chọn độ tuổi" value={customAgeGroups} onChange={setCustomAgeGroups}
                      options={AGE_GROUP_OPTIONS} allowClear maxTagCount="responsive" />
                  </Form.Item>
                  <Form.Item label="Chức năng truy cập" labelAlign="left">
                    <Select mode="multiple" placeholder="Chọn chức năng truy cập" value={customFeatures} onChange={setCustomFeatures}
                      options={FEATURE_OPTIONS} allowClear maxTagCount="responsive" />
                  </Form.Item>
                  <Form.Item label="User ID" labelAlign="left">
                    <Input placeholder="Nhập User ID (ngăn cách bằng ;)" value={customUserIds}
                      onChange={e => setCustomUserIds(e.target.value)} allowClear />
                  </Form.Item>
                </Col>

                {/* Right Column per Figma */}
                <Col xs={24} lg={12}>
                  <Form.Item label="Số điện thoại" labelAlign="left">
                    <Input placeholder="Nhập số điện thoại" value={customPhone} onChange={e => setCustomPhone(e.target.value)} allowClear />
                  </Form.Item>
                  <Form.Item label="Giới tính" labelAlign="left">
                    <Select mode="multiple" placeholder="Chọn giới tính" value={customGenders} onChange={setCustomGenders}
                      options={GENDER_OPTIONS} allowClear maxTagCount="responsive" />
                  </Form.Item>
                  <Form.Item label="Tỉnh thành" labelAlign="left">
                    <Select mode="multiple" placeholder="Chọn tỉnh thành" value={customProvinces} onChange={setCustomProvinces}
                      options={PROVINCE_OPTIONS} allowClear showSearch
                      filterOption={(input, opt) => (opt?.label as string)?.toLowerCase().includes(input.toLowerCase())}
                      maxTagCount="responsive" />
                  </Form.Item>
                  <Form.Item label="Thiết bị truy cập" labelAlign="left">
                    <Select mode="multiple" placeholder="Chọn thiết bị truy cập" value={customDevices} onChange={setCustomDevices}
                      options={DEVICE_OPTIONS} allowClear maxTagCount="responsive" />
                  </Form.Item>
                  <Form.Item label="Thời điểm truy cập" labelAlign="left">
                    <div css={dateRangeStyle}>
                      <DatePicker
                        placeholder="Từ ngày"
                        format="DD/MM/YYYY"
                        value={customAccessRange[0] ? dayjs(customAccessRange[0]) : null}
                        onChange={(date) => setCustomAccessRange([date ? date.format('YYYY-MM-DD') : null, customAccessRange[1]])}
                        style={{ width: '48%' }}
                        allowClear
                      />
                      <span style={{ margin: '0 4px', color: '#8c8c8c' }}>-</span>
                      <DatePicker
                        placeholder="Đến ngày"
                        format="DD/MM/YYYY"
                        value={customAccessRange[1] ? dayjs(customAccessRange[1]) : null}
                        onChange={(date) => setCustomAccessRange([customAccessRange[0], date ? date.format('YYYY-MM-DD') : null])}
                        style={{ width: '48%' }}
                        allowClear
                      />
                    </div>
                  </Form.Item>
                </Col>
              </Row>

              {/* Action Buttons Centered at Bottom per Figma */}
              <div css={customActionButtonsStyle}>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleCustomReset}
                  style={{
                    borderRadius: 6,
                    minWidth: 100,
                    marginRight: 16,
                    borderColor: '#1890ff',
                    color: '#1890ff',
                    fontWeight: 500,
                  }}
                >
                  Đặt lại
                </Button>
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={handleCustomSearch}
                  loading={listLoading}
                  style={{
                    borderRadius: 6,
                    minWidth: 110,
                    backgroundColor: '#1890ff',
                    borderColor: '#1890ff',
                    fontWeight: 500,
                  }}
                >
                  Tìm kiếm
                </Button>
              </div>
            </Form>
          </div>
        )}
      </Card>

        {/* 4. Session Table */}
        <AccessSessionTable
          loading={listLoading}
          data={listData}
          page={page}
          pageSize={pageSize}
          onPageChange={(p, ps) => { setPage(p); setPageSize(ps); }}
        />
      </div>
    );
  }
);

const mainCardStyle = css`
  margin-bottom: 16px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

  .ant-card-body {
    padding: 16px 20px 24px 20px;
  }
`;

const viewByRowStyle = css`
  margin: 0 -20px 16px;
  padding: 0 20px 16px;
  border-bottom: 1px solid #f0f0f0;

  .xem-theo-select {
    display: inline-block;
  }

  .xem-theo-select .ant-select-selector {
    background-color: #1890ff !important;
    color: #fff !important;
    border-color: #1890ff !important;
    border-radius: 4px;
    font-weight: 600;
  }

  .xem-theo-select .ant-select-selection-item {
    color: #fff !important;
    font-weight: 600;
  }

  .xem-theo-select .ant-select-arrow {
    color: #fff !important;
  }
`;

const headerStyle = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 12px;
`;

const titleSectionStyle = css`
  display: flex;
  flex-direction: column;
`;

const filterSectionStyle = css`
  display: flex;
  justify-content: flex-end;
  padding-right: 12px;
`;

const emptyStyle = css`
  text-align: center;
  color: #bfbfbf;
  padding: 40px 0;
`;

const legendStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const legendBoxStyle = css`
  width: 12px;
  height: 12px;
  background-color: ${PRIMARY_BAR_COLOR};
  border-radius: 2px;
  display: inline-block;
`;

const dateRangeStyle = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const customActionButtonsStyle = css`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 16px;
  padding-top: 8px;
`;
